import type { ThemeConfig } from "@/types/theme";

export const tailwindGreen: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-green-700 dark:text-green-400",
    secondary: "text-green-900 dark:text-green-500",
    accent: "text-teal-600 dark:text-teal-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-green-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-green-600 dark:bg-green-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-green-50 to-teal-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-green-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-green-600 dark:bg-green-500",
    backgroundSecondaryColor: "bg-green-800 dark:bg-green-700",
    backgroundAccentColor: "bg-teal-500 dark:bg-teal-400",
    backgroundPrimarySubtle: "bg-green-50 dark:bg-green-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-green-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-green-700 dark:text-green-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-green-600 dark:text-green-400",
    iconBackgroundFilled: "bg-green-100 dark:bg-green-950",
    iconBackgroundGradient: "bg-gradient-to-br from-green-500 to-teal-500",
    iconBackgroundDefault: "bg-green-50 dark:bg-green-950/50",
    iconBorder: "border-green-200 dark:border-green-800",

    // Border classes
    borderDefault: "border-green-200 dark:border-gray-700",
    borderLight: "border-green-100 dark:border-gray-800",
    borderStrong: "border-green-300 dark:border-gray-600",
    borderPrimary: "border-green-700 dark:border-green-500",
    borderSecondary: "border-green-900 dark:border-green-700",
    borderBottom: "border-b border-green-200 dark:border-gray-700",
    borderAccent: "border-green-600 dark:border-green-400",

    // Badge classes
    badgeBackground: "bg-green-100 dark:bg-green-950",
    badgeText: "text-green-700 dark:text-green-300",

    // Shadow classes
    shadowPrimary: "shadow-green-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-green-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(34,197,94,0.30)]", // green-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-green-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-green-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-green-400 dark:bg-green-600",
    textEmphasis: "text-fuchsia-600 dark:text-fuchsia-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-green-600 to-green-700",
    gradientSecondary: "bg-gradient-to-br from-green-700 to-green-900",
    gradientAccent: "bg-gradient-to-r from-green-500 to-teal-500",
    gradientSubtle:
      "bg-gradient-to-br from-green-50 to-teal-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-green-600 via-teal-500 to-green-600",
    gradientHero: "bg-gradient-to-br from-green-600 via-teal-500 to-green-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (kept consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
